import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { paymentsApi, type PaymentAnomaliesSummary } from '../../lib/api/payments';

interface Props {
  standortId?: number;
  daysWindow?: number;
  /**
   * Wenn true, wird der innere Inhalt ohne Card/Header gerendert.
   * Für Einbettung in das Dashboard-Widget-Grid, das einen eigenen
   * Card-Wrapper mit Drag-Handle bereitstellt.
   */
  embedded?: boolean;
}

export default function PaymentAnomaliesWidget({ standortId, daysWindow = 7, embedded = false }: Props) {
  const content = <PaymentAnomaliesContent standortId={standortId} daysWindow={daysWindow} />;
  if (embedded) {
    return content;
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Zahlungs-Anomalien (letzte {daysWindow} Tage)
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

/**
 * Reiner Content-Teil ohne Card/Header — kann in bestehende Widget-Grid-Wrapper
 * eingebettet werden, die ihren eigenen Header mit Drag-Handle mitbringen.
 */
export function PaymentAnomaliesContent({ standortId, daysWindow = 7 }: { standortId?: number; daysWindow?: number }) {
  const [summary, setSummary] = useState<PaymentAnomaliesSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const since = new Date();
    since.setDate(since.getDate() - daysWindow);
    const sinceStr = since.toISOString().slice(0, 10);

    paymentsApi
      .anomaliesSummary({ standortId, since: sinceStr })
      .then((s) => setSummary(s))
      .catch((e) => setError(e instanceof Error ? e.message : 'Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [standortId, daysWindow]);

  const totalIssues = summary
    ? summary.openDisputes + summary.refunds + summary.failedPayments + summary.paymentMethodIssues
    : 0;

  return (
    <div className="space-y-2">
      {loading && <p className="text-sm text-muted-foreground">Lade…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {summary && (
        <>
          {totalIssues === 0 && (
            <p className="text-sm text-muted-foreground">Alles ruhig — keine Anomalien.</p>
          )}
          {summary.openDisputes > 0 && (
            <Row label="Offene Widersprüche" value={summary.openDisputes} severity="high" />
          )}
          {summary.failedPayments > 0 && (
            <Row label="Fehlgeschlagene Zahlungen" value={summary.failedPayments} severity="high" />
          )}
          {summary.paymentMethodIssues > 0 && (
            <Row label="Zahlungsmittel-Probleme" value={summary.paymentMethodIssues} severity="medium" />
          )}
          {summary.refunds > 0 && (
            <Row label="Erstattungen" value={summary.refunds} severity="low" />
          )}
          {totalIssues > 0 && (
            <a
              href="/dashboard/accounting/payments"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
            >
              Alle anzeigen <ArrowRight className="h-3 w-3" />
            </a>
          )}
        </>
      )}
    </div>
  );
}

function Row({ label, value, severity }: { label: string; value: number; severity: 'high' | 'medium' | 'low' }) {
  const colors = {
    high: 'text-destructive',
    medium: 'text-amber-600',
    low: 'text-muted-foreground',
  };
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={colors[severity]}>{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}
